/**
 * Invoice Ledger View - Shows ledger entries for a specific invoice
 */

import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, TextField } from "@mui/material";
import { useInvoiceLedger } from "../hooks/useLedger.js";
import LedgerTimeline from "../components/LedgerTimeline.jsx";
import {
  PageShell,
  PageHeader,
  PageFilters,
} from "@/shared/components/pageShell";

const InvoiceLedgerView = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);

  const asOfDateEndOfDay = useMemo(() => {
    if (!asOfDate) return new Date();
    const date = new Date(asOfDate);
    date.setHours(23, 59, 59, 999);
    return date;
  }, [asOfDate]);

  const { data, isLoading, error } = useInvoiceLedger(invoiceId, {
    asOfDate: asOfDateEndOfDay,
  });

  const entries = data?.success ? data.data : [];
  const errorMessage = error?.message || (data && !data.success ? data.error : null);

  return (
    <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
      <PageHeader
        title="Invoice Ledger"
        back={{
          label: "Back to Invoice",
          shortLabel: "Invoice",
          onClick: () => navigate(`/invoices/${invoiceId}`),
        }}
      />

      <PageFilters>
        <TextField
          label="As of Date"
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 200, bgcolor: "background.paper" }}
        />
      </PageFilters>

      <Box>
        <LedgerTimeline
          entries={entries}
          loading={isLoading}
          error={errorMessage}
          title={`Financial Timeline for Invoice ${invoiceId}`}
        />
      </Box>
    </PageShell>
  );
};

export default InvoiceLedgerView;
